import { createContext, useContext, useReducer, useCallback, useMemo, useEffect, useState, useRef } from 'react';
import type { TimerSettings, TimerContextType, TimerStage, ThemeMode } from '../types/timer';
import { applyTheme, getStoredTheme } from '../utils/theme';
import { audioManager } from '../utils/audio';
import { bootstrapAccount } from '../lib/bootstrap';
import { recordCompletedStage } from '../lib/sessions';
import { saveRemotePreferences } from '../lib/preferences';
import { mirrorTimerStateToSW, querySWTimerState } from '../lib/swTimerBridge';

// Import worker message types (adjust path if needed)
// Assuming vite setup where '.worker.ts' is handled
// You might need /// <reference lib="webworker" /> in a global d.ts file if TS complains about Worker
import type { WorkerCommand, WorkerEvent, WorkerState } from '../workers/timer.worker';
import { debugLog } from '../lib/debugLog';

// Type for state persisted to localStorage (can be simplified)
interface PersistedState {
  // Only persist settings and perhaps minimal state needed for initial worker hydration
  settings: TimerSettings;
  // Add last known state if needed for quick display before worker syncs
  // stage?: TimerState['stage'];
  // completedPomodoros?: number;
  // totalCompletedCycles?: number;
  // currentPomodoro?: number;
}

// React Context State - Primarily UI focused now
interface TimerState {
  stage: TimerStage;
  timeRemaining: number; // Display time (in seconds)
  isRunning: boolean;
  currentPomodoro: number; // Current pomodoro within the cycle (e.g., 1-4)
  completedPomodoros: number; // Total completed focus sessions
  settings: TimerSettings;
  totalCompletedCycles: number; // Completed full cycles
  isPremium: boolean; // Assuming this remains
  isLoading: boolean; // Flag for initial loading/worker sync
  shouldPlaySound: boolean; // Flag to trigger sound effect
  soundType?: 'focus' | 'shortBreak' | 'longBreak'; // Type of sound to play
}

const TimerContext = createContext<TimerContextType | null>(null);

// Default settings remain the same
const DEFAULT_SETTINGS: TimerSettings = {
  focusDuration: 25 * 60,
  shortBreakDuration: 5 * 60,
  longBreakDuration: 15 * 60,
  cycleCount: 4,
  autoAdvance: true,
  theme: getStoredTheme(),
  soundEnabled: true,
  soundStyle: 'smooth',
  notificationsEnabled: false,
  customSounds: {},
};

// Default state adjusted - timeRemaining might be set by worker sync
const DEFAULT_STATE: TimerState = {
  stage: 'focus',
  timeRemaining: DEFAULT_SETTINGS.focusDuration, // Initial display guess
  isRunning: false,
  currentPomodoro: 1,
  completedPomodoros: 0,
  settings: DEFAULT_SETTINGS,
  totalCompletedCycles: 0,
  isPremium: false,
  isLoading: true, // Start as loading until worker syncs
  shouldPlaySound: false,
  soundType: undefined,
};

// Actions adjusted - Less direct timer control, more syncing with worker
type TimerAction =
  | { type: 'SYNC_STATE'; payload: WorkerState & { playSoundOnSync?: boolean; soundTypeOnSync?: TimerState['soundType'] } } // Sync entire state from worker
  | { type: 'UPDATE_DISPLAY_TIME'; payload: { timeRemaining: number } } // Only update display time from worker ticks
  | { type: 'TRIGGER_SOUND'; payload: { type: TimerState['soundType'] } } // Trigger sound playback
  | { type: 'UPDATE_SETTINGS_UI'; payload: Partial<TimerSettings> } // Update UI immediately on settings change
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_PREMIUM'; payload: boolean }
  | { type: 'RESET_SOUND_FLAGS' }; // Action to clear sound flags after playback

// Helper: getDurationKey can remain if needed for UI logic, but worker uses its own
function getDurationKey(stage: TimerStage): keyof Pick<TimerSettings, 'focusDuration' | 'shortBreakDuration' | 'longBreakDuration'> {
  switch (stage) {
    case 'focus': return 'focusDuration';
    case 'shortBreak': return 'shortBreakDuration';
    case 'longBreak': return 'longBreakDuration';
  }
}


// --- Reducer --- (Simplified: Reacts to worker messages)
function timerReducer(state: TimerState, action: TimerAction): TimerState {
  switch (action.type) {
    case 'SYNC_STATE': {
       const { playSoundOnSync, soundTypeOnSync, ...workerState } = action.payload;
       // debugLog("[Reducer] SYNC_STATE received:", workerState);
        return {
            ...state,
            ...workerState, // Overwrite state with worker's source of truth
            timeRemaining: Math.max(0, workerState.timeRemaining), // Ensure non-negative display
            settings: { // Deep merge settings to be safe
                ...state.settings,
                ...workerState.settings,
            },
            isLoading: false, // Mark loading complete after first sync
            shouldPlaySound: !!playSoundOnSync && workerState.settings.soundEnabled, // Check if sound should play on this sync
            soundType: playSoundOnSync ? soundTypeOnSync : undefined,
        };
    }
    case 'UPDATE_DISPLAY_TIME':
      // Only update timeRemaining if the timer is actually running according to state
      // This prevents flicker if a TICK message arrives slightly after a PAUSE sync
      return state.isRunning ? {
        ...state,
        timeRemaining: Math.max(0, action.payload.timeRemaining),
      } : state;

    case 'TRIGGER_SOUND':
        return {
            ...state,
            shouldPlaySound: state.settings.soundEnabled, // Only trigger if globally enabled
            soundType: action.payload.type,
        };

    case 'UPDATE_SETTINGS_UI': {
      const newSettings = { ...state.settings, ...action.payload };
      let nextTimeRemaining = state.timeRemaining;

      // If timer is NOT running, update time display based on new duration for current stage
      if (!state.isRunning) {
          const currentStageDurationKey = getDurationKey(state.stage);
          if (currentStageDurationKey in action.payload) {
               nextTimeRemaining = newSettings[currentStageDurationKey];
          }
      }

      return {
        ...state,
        settings: newSettings,
        timeRemaining: nextTimeRemaining, // Update UI display optimistically
      };
    }

    case 'SET_LOADING':
        return { ...state, isLoading: action.payload };

    case 'SET_PREMIUM':
        return { ...state, isPremium: action.payload };

    case 'RESET_SOUND_FLAGS':
        return { ...state, shouldPlaySound: false, soundType: undefined };

    default:
      return state;
  }
}

// --- Background Simulation Removed ---

// --- Provider Component --- (Refactored for Worker)
export function TimerProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(timerReducer, DEFAULT_STATE);
  const { settings, isLoading, stage, isRunning } = state; // Destructure for effects

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Optional "what are you working on" label attached to recorded sessions.
  // A ref mirror lets the worker-message handler read the latest value
  // without re-subscribing the worker listener.
  const [sessionLabel, setSessionLabel] = useState('');
  const sessionLabelRef = useRef(sessionLabel);
  sessionLabelRef.current = sessionLabel;
  // isVisible state is less critical for timer logic, but can be kept for UI/optimizations
  const [isVisible, setIsVisible] = useState(document.visibilityState === 'visible');

  const workerRef = useRef<Worker | null>(null);
  const stateRef = useRef(state); // Latest state for async handlers (visibility reconcile)
  const audioSessionRef = useRef<AudioSession | null>(null); // Keep for sound playback
  const soundResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveStateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // For debouncing save
  const savePrefsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const changedSettingsKeysRef = useRef(new Set<keyof TimerSettings>());
  const accountRefreshVersionRef = useRef(0);

  // Keep stateRef in sync for async handlers
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // --- Post Message Helper ---
  const postWorkerCommand = useCallback((command: WorkerCommand) => {
      if (workerRef.current) {
          // debugLog("[Context] Posting command to worker:", command);
          workerRef.current.postMessage(command);
      } else {
          console.warn("[Context] Worker not available to post command:", command);
      }
  }, []);


  // --- Save State Helper --- (Saves state received from worker/UI)
  const saveCurrentState = useCallback(() => {
      if (isLoading) return; // Don't save during initial load
      // Save only essential settings, worker will reconstruct timer state
      const stateToSave: PersistedState = {
        settings: state.settings, // Save the latest settings
        // Optionally save last known state for faster initial render before worker sync
        // stage: state.stage,
        // completedPomodoros: state.completedPomodoros,
        // totalCompletedCycles: state.totalCompletedCycles,
        // currentPomodoro: state.currentPomodoro,
      };
      // debugLog("[Save State] Saving state to localStorage:", stateToSave);
      localStorage.setItem('pomodoroAppState', JSON.stringify(stateToSave));
  }, [state.settings, isLoading /* add other state fields if saving them */ ]);

  // --- Worker Initialization and Message Handling ---
  useEffect(() => {
    let cancelled = false;

    async function initializeTimer() {
      // Account requests can be slow or offline. Start them now, but only wait
      // for the service worker snapshot needed to restore the live timer.
      const accountPromise = bootstrapAccount().catch((error) => {
        console.warn('[Initialize] Could not load account settings:', error);
        return { isPremium: localStorage.getItem('is_premium') === 'true', settings: {} };
      });

      // Load settings from localStorage
      let loadedSettings = { ...DEFAULT_SETTINGS };
      let persistedState: PersistedState | null = null;
      const savedStateJSON = localStorage.getItem('pomodoroAppState');
      if (savedStateJSON) {
        try {
          persistedState = JSON.parse(savedStateJSON);
          if (persistedState?.settings) {
            loadedSettings = {
              ...DEFAULT_SETTINGS,
              ...persistedState.settings,
              customSounds: {
                ...(DEFAULT_SETTINGS.customSounds || {}),
                ...(persistedState.settings.customSounds || {}),
              },
            };
            debugLog("[Initialize] Loaded settings from localStorage:", loadedSettings);
          } else {
            debugLog("[Initialize] No settings found in saved state, using defaults.");
          }
        } catch (error) {
          console.error("Failed to parse saved state:", error);
        }
      } else {
        debugLog("[Initialize] No saved state found, using default settings.");
      }

      const cachedPremium = localStorage.getItem('is_premium') === 'true';
      const persistedCustomSounds = loadedSettings.customSounds;
      dispatch({ type: 'SET_PREMIUM', payload: cachedPremium });
      if (!cachedPremium) {
        loadedSettings.customSounds = {};
      }

      dispatch({ type: 'UPDATE_SETTINGS_UI', payload: loadedSettings });

      if (cancelled) return;

      // If a timer was running when the app was last closed, the service worker
      // still knows about it (timestamp-based state in IndexedDB). Restore it so
      // the timer picks up exactly where it should be.
      let restoredState: Partial<WorkerState> | undefined;
      try {
        const swState = await querySWTimerState();
        if (swState) {
          restoredState = {
            stage: swState.stage,
            timeRemaining: Math.max(0, Math.round(swState.calculatedRemaining)),
            isRunning: swState.isRunning && swState.calculatedRemaining > 0,
            currentPomodoro: swState.currentPomodoro,
            completedPomodoros: swState.completedPomodoros,
            totalCompletedCycles: swState.totalCompletedCycles,
          };
          debugLog('[Initialize] Restored timer state from service worker:', restoredState);
        }
      } catch (error) {
        console.warn('[Initialize] Could not restore timer state from service worker:', error);
      }
      if (cancelled) return;

      const worker = new Worker(new URL('../workers/timer.worker.ts', import.meta.url), { type: 'module' });
      workerRef.current = worker;
      debugLog("[Initialize] Worker created.");

      postWorkerCommand({ type: 'INIT', payload: { settings: loadedSettings, initialState: restoredState } });

      worker.onmessage = (event: MessageEvent<WorkerEvent>) => {
      const { type, payload } = event.data;
      // debugLog("[Context] Received message from worker:", type, payload);

      switch (type) {
        case 'TICK':
          dispatch({ type: 'UPDATE_DISPLAY_TIME', payload: { timeRemaining: payload.timeRemaining } });
          break;
        case 'TRANSITION': {
          // Worker signals a transition. Sync state and trigger sound.
          const { shouldPlayAudio, previousStage, reason, ...workerState } = payload;
          mirrorTimerStateToSW(workerState); // Keep SW's durable copy in sync

          // Record completed stages in session history (skip = not completed).
          // Fire-and-forget — history failures must never affect the timer.
          if (reason === 'completed') {
            const durationKey =
              previousStage === 'focus'
                ? 'focusDuration'
                : previousStage === 'shortBreak'
                  ? 'shortBreakDuration'
                  : 'longBreakDuration';
            recordCompletedStage(
              previousStage,
              workerState.settings[durationKey],
              // The label describes focus work — only attach it to focus stages.
              previousStage === 'focus'
                ? sessionLabelRef.current.trim() || undefined
                : undefined,
            );
          }

          dispatch({
            type: 'SYNC_STATE',
            payload: {
                ...workerState,
                // Determine sound type based on the *new* stage the worker transitioned *to*
                soundTypeOnSync: workerState.stage,
                playSoundOnSync: shouldPlayAudio // Use the flag sent by the worker
            }
          });
          // Handle notifications based on transition
          if (workerState.settings.notificationsEnabled && 'Notification' in window && Notification.permission === 'granted' && isVisible === false) {
             try {
                 const formatStageName = (s: TimerStage) => s.charAt(0).toUpperCase() + s.slice(1).replace(/([A-Z])/g, ' $1');
                 const notificationTitle = `${formatStageName(previousStage)} Complete!`; // Use previousStage from payload
                 const nextStageLabel = formatStageName(workerState.stage); // Use new stage from payload
                 const notificationBody = workerState.settings.autoAdvance ? `Starting ${nextStageLabel}...` : `Ready for ${nextStageLabel}.`;
                 new Notification(notificationTitle, {
                     body: notificationBody,
                     tag: 'pomodoro-stage-complete',
                 });
                debugLog(`[Notification] Sent: ${notificationTitle} - ${notificationBody}`);
             } catch (err) {
                 console.error("Error showing notification:", err);
             }
          }
          break;
        }
        case 'STATE_SYNC':
          // Worker sends its full current state (e.g., after init, start, pause, reset, settings change)
          mirrorTimerStateToSW(payload); // Keep SW's durable copy in sync
          dispatch({ type: 'SYNC_STATE', payload: payload });
           // Debounce saving state after receiving a sync
           if (saveStateTimeoutRef.current) clearTimeout(saveStateTimeoutRef.current);
           saveStateTimeoutRef.current = setTimeout(saveCurrentState, 1000);
          break;
        // default: console.warn("[Context] Unknown worker message type:", type);
      }
    };

    // Error Handling
    worker.onerror = (error) => {
      console.error("[Worker Error]", error);
      dispatch({ type: 'SET_LOADING', payload: false });
    };

      const refreshVersion = accountRefreshVersionRef.current;
      void accountPromise.then((account) => {
        if (cancelled || refreshVersion !== accountRefreshVersionRef.current) return;

        dispatch({ type: 'SET_PREMIUM', payload: account.isPremium });
        if (account.isPremium) {
          localStorage.setItem('is_premium', 'true');
        } else {
          localStorage.removeItem('is_premium');
        }

        // A setting changed while the request was in flight belongs to the user.
        const remoteSettings = Object.fromEntries(
          Object.entries(account.settings).filter(([key]) =>
            !changedSettingsKeysRef.current.has(key as keyof TimerSettings)),
        ) as Partial<TimerSettings>;
        if (account.isPremium && !changedSettingsKeysRef.current.has('customSounds')) {
          remoteSettings.customSounds = {
            ...persistedCustomSounds,
            ...remoteSettings.customSounds,
          };
        } else if (!account.isPremium) {
          remoteSettings.customSounds = {};
        }
        if (Object.keys(remoteSettings).length > 0) {
          dispatch({ type: 'UPDATE_SETTINGS_UI', payload: remoteSettings });
          postWorkerCommand({ type: 'UPDATE_SETTINGS', payload: remoteSettings });
        }
      }).catch((error) => {
        console.warn('[Initialize] Could not load account settings:', error);
      });
    }

    initializeTimer();

    // Cleanup
    return () => {
      cancelled = true;
      debugLog("[Cleanup] Terminating worker.");
      workerRef.current?.terminate();
      workerRef.current = null;
      if (soundResetTimeoutRef.current) clearTimeout(soundResetTimeoutRef.current);
      if (saveStateTimeoutRef.current) clearTimeout(saveStateTimeoutRef.current);
      if (savePrefsTimeoutRef.current) clearTimeout(savePrefsTimeoutRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // --- Visibility Change Handler --- (Request state sync from worker)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        setIsVisible(true);
        debugLog('[Visibility] Became visible. Reconciling with service worker.');

        // Ask the SW first: it tracks time by timestamp and survives page
        // freezes, so it knows the true elapsed time even if the web worker's
        // interval was suspended. (Query BEFORE touching the worker, so a stale
        // worker state doesn't get mirrored over the SW's correct one.)
        try {
          const swState = await querySWTimerState();
          const current = stateRef.current;
          if (swState) {
            const drifted =
              swState.stage !== current.stage ||
              swState.isRunning !== current.isRunning ||
              Math.abs(swState.calculatedRemaining - current.timeRemaining) > 3;
            if (drifted) {
              debugLog('[Visibility] Worker drifted from SW state. Restoring:', swState);
              postWorkerCommand({
                type: 'RESTORE',
                payload: {
                  stage: swState.stage,
                  timeRemaining: Math.max(0, Math.round(swState.calculatedRemaining)),
                  isRunning: swState.isRunning && swState.calculatedRemaining > 0,
                  currentPomodoro: swState.currentPomodoro,
                  completedPomodoros: swState.completedPomodoros,
                  totalCompletedCycles: swState.totalCompletedCycles,
                },
              });
            } else {
              postWorkerCommand({ type: 'REQUEST_STATE' });
            }
          } else {
            postWorkerCommand({ type: 'REQUEST_STATE' }); // No SW answer; fall back to worker state
          }
        } catch {
          postWorkerCommand({ type: 'REQUEST_STATE' });
        }

         // Attempt audio resume (if needed by audioSessionRef)
         if (audioSessionRef.current) {
            try { await audioSessionRef.current.startAudioSession(); } catch (error) { /* ... */ }
         }
         // Safari can suspend the AudioManager's context independently of the
         // keep-alive session when returning from the background.
         void audioManager.ensureInitialized();
      } else {
        setIsVisible(false);
        debugLog('[Visibility] Became hidden. Saving current state.');
        // Save the *last known state* before hiding. Worker continues independently.
        saveCurrentState();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [postWorkerCommand, saveCurrentState]); // Dependencies


  // --- Timer Actions --- (Send commands to worker)

  const startTimer = useCallback(() => {
    // Stop any current sound playback immediately when user manually starts
    audioManager.stopCurrentPlayback();
    postWorkerCommand({ type: 'START' });
  }, [postWorkerCommand]);

  const pauseTimer = useCallback(() => {
    // Stop any current sound playback immediately when user manually pauses
    audioManager.stopCurrentPlayback();
    postWorkerCommand({ type: 'PAUSE' });
  }, [postWorkerCommand]);

  const skipStage = useCallback(() => {
    // Stop any current sound playback immediately when user manually skips
    audioManager.stopCurrentPlayback();
    postWorkerCommand({ type: 'SKIP' });
  }, [postWorkerCommand]);

  const resetTimer = useCallback(() => {
    audioManager.stopCurrentPlayback();
    postWorkerCommand({ type: 'RESET' });
  }, [postWorkerCommand]);

  const resetAllPomodoros = useCallback(() => {
    audioManager.stopCurrentPlayback();
    postWorkerCommand({ type: 'RESET_ALL' });
  }, [postWorkerCommand]);

  // Tab-title countdown — see the timer from another tab.
  useEffect(() => {
    const stageLabel =
      state.stage === 'focus' ? 'Focus' : state.stage === 'shortBreak' ? 'Short Break' : 'Long Break';
    if (state.isRunning) {
      const m = Math.floor(state.timeRemaining / 60);
      const s = state.timeRemaining % 60;
      document.title = `${m}:${String(s).padStart(2, '0')} ${stageLabel} — Pomodingo`;
    } else {
      document.title = 'Pomodingo';
    }
  }, [state.isRunning, state.timeRemaining, state.stage]);

  // Keep the screen awake while a pomodoro is running.
  useEffect(() => {
    if (!state.isRunning) return;
    let sentinel: { release: () => Promise<void> } | null = null;
    let released = false;
    const acquire = async () => {
      try {
        const wakeLock = (navigator as unknown as {
          wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> };
        }).wakeLock;
        if (wakeLock) {
          sentinel = await wakeLock.request('screen');
        }
      } catch {
        // Unsupported or denied — non-critical.
      }
    };
    void acquire();
    return () => {
      void sentinel?.release().catch(() => undefined);
      sentinel = null;
      void released;
    };
  }, [state.isRunning]);

  // Guard against accidentally closing the tab mid-pomodoro.
  useEffect(() => {
    if (!state.isRunning) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state.isRunning]);

  const refreshAccountState = useCallback(async () => {
    accountRefreshVersionRef.current += 1;
    const account = await bootstrapAccount();
    dispatch({ type: 'SET_PREMIUM', payload: account.isPremium });

    if (account.isPremium) {
      localStorage.setItem('is_premium', 'true');
    } else {
      localStorage.removeItem('is_premium');
      localStorage.removeItem('user_id');
    }

    if (Object.keys(account.settings).length > 0) {
      dispatch({ type: 'UPDATE_SETTINGS_UI', payload: account.settings });
      postWorkerCommand({ type: 'UPDATE_SETTINGS', payload: account.settings });
    }
  }, [postWorkerCommand]);

  const updateSettings = useCallback((newSettings: Partial<TimerSettings>) => {
    Object.keys(newSettings).forEach((key) =>
      changedSettingsKeysRef.current.add(key as keyof TimerSettings));
    // Update UI optimistically
    dispatch({ type: 'UPDATE_SETTINGS_UI', payload: newSettings });
    // Send update to worker
    postWorkerCommand({ type: 'UPDATE_SETTINGS', payload: newSettings });
    // Settings change might affect theme or custom sounds, apply those immediately
    if (newSettings.theme !== undefined) {
        applyTheme(newSettings.theme === 'system' ? getStoredTheme() : newSettings.theme);
    }
     if (newSettings.customSounds !== undefined) {
         Object.entries(newSettings.customSounds).forEach(([type, path]) => {
             if ((type === 'focus' || type === 'shortBreak' || type === 'longBreak')) {
                 audioManager.setCustomSound(type, path || '');
             }
         });
     }
     if (newSettings.soundStyle !== undefined) {
         audioManager.soundStyle = newSettings.soundStyle;
     }

     if (savePrefsTimeoutRef.current) clearTimeout(savePrefsTimeoutRef.current);
     savePrefsTimeoutRef.current = setTimeout(() => {
       saveRemotePreferences(newSettings).catch((error) => {
         console.warn('[Preferences] Failed to save remote preferences:', error);
       });
     }, 1000);
  }, [postWorkerCommand]);


  // --- Play Sound Effect --- (Triggered by SYNC_STATE with playSoundOnSync flag)
  useEffect(() => {
    if (state.shouldPlaySound && state.soundType && settings.soundEnabled && !isLoading) {
      let soundPlayed = false;
      if (soundResetTimeoutRef.current) {
        clearTimeout(soundResetTimeoutRef.current);
        soundResetTimeoutRef.current = null;
      }
      async function playSound() {
        if (soundPlayed) return; // Prevent double playback if effect re-runs quickly
        soundPlayed = true;
        try {
          // debugLog(`[Audio] Attempting to play sound: ${state.soundType}`);
          // Assuming audioSessionRef setup happens elsewhere or is handled by visibility change
          switch (state.soundType) {
            case 'focus': await audioManager.playFocusSound(true); break;
            case 'shortBreak': await audioManager.playBreakSound(true); break;
            case 'longBreak': await audioManager.playLongBreakSound(true); break;
          }
          // debugLog(`[Audio] Sound ${state.soundType} played successfully.`);
        } catch (error) {
          console.warn('[Audio] Failed to play sound:', error);
        } finally {
           // Use a timeout to reset the sound flags shortly after attempting to play
           soundResetTimeoutRef.current = setTimeout(() => {
                dispatch({ type: 'RESET_SOUND_FLAGS' });
                soundResetTimeoutRef.current = null;
            }, 500); // Increased timeout slightly
        }
      }
      playSound();
    }
     // Cleanup timeout if component unmounts or deps change before timeout fires
     return () => {
         if (soundResetTimeoutRef.current) {
            clearTimeout(soundResetTimeoutRef.current);
         }
     };
    // Only depend on the flags triggering the sound, not the whole state object
  }, [state.shouldPlaySound, state.soundType, settings.soundEnabled, isLoading]);


  // --- Theme Effects --- (Largely unchanged, ensure theme updates)
    useEffect(() => {
        if (!isLoading && settings.theme) {
             applyTheme(settings.theme);
        }
    }, [settings.theme, isLoading]);

    useEffect(() => {
        if (isLoading || settings.theme !== 'system') return;
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = () => applyTheme('system'); // Re-apply system theme on change
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [settings.theme, isLoading]);

   // Effect to sync initial/updated custom sound settings with AudioManager
   // Run this after worker is initialized and settings are potentially loaded/synced
   useEffect(() => {
       if (!isLoading && state.isPremium && settings.customSounds) {
           debugLog("[Context] Syncing custom sounds with AudioManager:", settings.customSounds);
           Object.entries(settings.customSounds).forEach(([type, path]) => {
               if ((type === 'focus' || type === 'shortBreak' || type === 'longBreak')) {
                   audioManager.setCustomSound(type, path || '');
               }
           });
       }
   }, [isLoading, state.isPremium, settings.customSounds]);


  // --- Memoized Context Value ---
  const value = useMemo(() => ({
    settings,
    state, // Provide the latest UI state
    startTimer,
    pauseTimer,
    skipStage,
    resetTimer,
    resetAllPomodoros,
    updateSettings,
    refreshAccountState,
    showUpgradeModal,
    setShowUpgradeModal,
    sessionLabel,
    setSessionLabel,
  }), [
    settings,
    state,
    startTimer,
    pauseTimer,
    skipStage,
    resetTimer,
    resetAllPomodoros,
    updateSettings,
    refreshAccountState,
    showUpgradeModal,
    sessionLabel,
  ]);


  return (
    <TimerContext.Provider value={value}>
      {isLoading ? (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white" role="status" aria-live="polite">
          Syncing Timer...
        </div>
      ) : children}
    </TimerContext.Provider>
  );
}

// --- Hook --- (Unchanged)
export function useTimer() {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
}

// Define AudioSession interface if not imported
interface AudioSession {
  startAudioSession: () => Promise<void>;
  stopAudioSession: () => void;
}
